import { createSlice } from '@reduxjs/toolkit';

// UI state for the "Ask Reggie" drawer. The drawer is global to the Analytics
// section; individual analytics pages publish what the user is looking at
// (page label, date range, active filters) so answers are anchored to it.
//
// Conversations live here (not in the panel) so closing the drawer keeps the
// thread. The service stores every turn per user+organization; the drawer loads
// the list from it when the identity changes and a thread's messages when it is
// opened. Nothing is kept in the browser between sessions.

export const MAX_CONVERSATIONS = 50;

const defaultContext = {
  page: 'Analytics',
  label: 'Analytics',
  filters: {},
  dateRange: null,
};

const initialState = {
  isOpen: false,
  view: 'chat', // chat | history
  context: defaultContext,
  storageKey: null, // identity key (user + org) the loaded conversations belong to
  conversations: {}, // id -> { id, title, messages, createdAt, updatedAt, turns, loaded }
  activeConversationId: null,
  usageMonth: null, // { month, turns, limit } for the organization, from the service
};

const titleFrom = (text) => {
  const t = (text || '').trim().replace(/\s+/g, ' ');
  return t.length > 70 ? `${t.slice(0, 67)}…` : t;
};

const hasContent = (c) => !!c && ((Array.isArray(c.messages) && c.messages.length > 0) || (c.turns || 0) > 0);

const prune = (conversations) => {
  const kept = Object.values(conversations)
    .filter(hasContent)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
    .slice(0, MAX_CONVERSATIONS);
  return Object.fromEntries(kept.map((c) => [c.id, c]));
};

// A stored message (service shape) -> the panel's message shape. Attachments the
// service had to truncate have no payload and are dropped rather than rendered empty.
const fromStoredMessage = (m) => ({
  id: `s${m.seq ?? m.id}`,
  role: m.role,
  content: m.content || '',
  page: m.page || null,
  toolCalls: (m.toolCalls || []).map((t) => ({ id: t.id, name: t.name, status: t.status === 'error' ? 'error' : 'done' })),
  attachments: (m.attachments || []).filter((a) => a && a.payload).map((a) => ({ id: a.id, kind: a.kind, payload: a.payload })),
  pending: false,
  error: m.error ? 'This answer could not be completed.' : undefined,
});

// Anything that was mid-flight when the page was last unloaded is finished now.
const settle = (conversation) => ({
  ...conversation,
  messages: (conversation.messages || []).map((m) => ({
    ...m,
    status: undefined,
    pending: false,
    toolCalls: (m.toolCalls || []).map((t) => (t.status === 'running' ? { ...t, status: 'done' } : t)),
  })),
});

const rhinoanalystSlice = createSlice({
  name: 'RHINOANALYST',
  initialState,
  reducers: {
    openAskReggie: (state) => ({ ...state, isOpen: true }),
    closeAskReggie: (state) => ({ ...state, isOpen: false }),
    toggleAskReggie: (state) => ({ ...state, isOpen: !state.isOpen }),
    setAskReggieView: (state, action) => ({ ...state, view: action.payload === 'history' ? 'history' : 'chat' }),
    setAskReggieContext: (state, action) => ({
      ...state,
      context: { ...defaultContext, ...(action.payload || {}) },
    }),
    clearAskReggieContext: (state) => ({ ...state, context: defaultContext }),

    // Called by the drawer when the (user, org) key changes, with the service's
    // conversation list (summaries only; messages load when a thread is opened).
    hydrateConversations: (state, action) => {
      const { storageKey, conversations } = action.payload;
      const list = Array.isArray(conversations) ? conversations : Object.values(conversations || {});
      const restored = prune(Object.fromEntries(list.map((c) => [c.id, settle({
        id: c.id,
        title: c.title || '',
        page: c.page || null,
        createdAt: c.createdAt || 0,
        updatedAt: c.updatedAt || 0,
        turns: c.turns || 0,
        messages: Array.isArray(c.messages) ? c.messages : [],
        loaded: Array.isArray(c.messages) && c.messages.length > 0,
      })])));
      return {
        ...state,
        storageKey,
        conversations: restored,
        activeConversationId: null,
        view: 'chat',
      };
    },
    receiveConversationMessages: (state, action) => {
      const { id, messages } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: { ...c, messages: (messages || []).map(fromStoredMessage), loaded: true },
        },
      };
    },
    setUsageMonth: (state, action) => ({ ...state, usageMonth: action.payload || null }),

    startConversation: (state, action) => {
      const { id, now } = action.payload;
      return {
        ...state,
        view: 'chat',
        activeConversationId: id,
        conversations: {
          ...state.conversations,
          [id]: {
            id, title: '', messages: [], createdAt: now, updatedAt: now, turns: 0, loaded: true,
          },
        },
      };
    },
    newConversation: (state) => ({ ...state, view: 'chat', activeConversationId: null }),
    activateConversation: (state, action) => ({
      ...state,
      view: 'chat',
      activeConversationId: state.conversations[action.payload] ? action.payload : state.activeConversationId,
    }),
    deleteConversation: (state, action) => {
      const { [action.payload]: removed, ...rest } = state.conversations;
      return {
        ...state,
        conversations: rest,
        activeConversationId: state.activeConversationId === action.payload ? null : state.activeConversationId,
      };
    },

    appendMessages: (state, action) => {
      const { id, messages, now } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      const firstUser = messages.find((m) => m.role === 'user');
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            title: c.title || titleFrom(firstUser?.content),
            messages: [...c.messages, ...messages],
            turns: (c.turns || 0) + (firstUser ? 1 : 0),
            updatedAt: now,
          },
        },
      };
    },
    patchMessage: (state, action) => {
      const {
        id, messageId, patch, appendText,
      } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            messages: c.messages.map((m) => (m.id === messageId
              ? { ...m, ...(patch || {}), content: appendText ? `${m.content}${appendText}` : (patch?.content ?? m.content) }
              : m)),
          },
        },
      };
    },
    // Text the model wrote before calling a tool ("I'll pull the call data...") is a
    // progress note, not the answer. Move it out of the bubble into the status line so
    // the typing indicator stays up until the real answer streams.
    demoteContentToStatus: (state, action) => {
      const { id, messageId } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            messages: c.messages.map((m) => {
              if (m.id !== messageId || !m.content || !m.content.trim()) return m;
              return { ...m, status: m.content.trim(), content: '' };
            }),
          },
        },
      };
    },
    patchToolCall: (state, action) => {
      const {
        id, messageId, toolCall,
      } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            messages: c.messages.map((m) => {
              if (m.id !== messageId) return m;
              const existing = (m.toolCalls || []).some((t) => t.id === toolCall.id);
              const toolCalls = existing
                ? m.toolCalls.map((t) => (t.id === toolCall.id ? { ...t, ...toolCall } : t))
                : [...(m.toolCalls || []), toolCall];
              return { ...m, toolCalls };
            }),
          },
        },
      };
    },
    addAttachment: (state, action) => {
      const { id, messageId, attachment } = action.payload;
      const c = state.conversations[id];
      if (!c) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [id]: {
            ...c,
            messages: c.messages.map((m) => (m.id === messageId
              ? { ...m, attachments: [...(m.attachments || []), attachment] }
              : m)),
          },
        },
      };
    },
  },
});

export default rhinoanalystSlice.reducer;

export const {
  openAskReggie,
  closeAskReggie,
  toggleAskReggie,
  setAskReggieView,
  setAskReggieContext,
  clearAskReggieContext,
  hydrateConversations,
  receiveConversationMessages,
  setUsageMonth,
  startConversation,
  newConversation,
  activateConversation,
  deleteConversation,
  appendMessages,
  patchMessage,
  patchToolCall,
  demoteContentToStatus,
  addAttachment,
} = rhinoanalystSlice.actions;

export const getAskReggieState = (state) => state.rhinoanalyst;
export const getActiveConversation = (state) => {
  const { conversations, activeConversationId } = state.rhinoanalyst;
  return activeConversationId ? conversations[activeConversationId] || null : null;
};
export const getConversationList = (state) => Object.values(state.rhinoanalyst.conversations)
  .filter(hasContent)
  .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
export const getUsageMonth = (state) => state.rhinoanalyst.usageMonth;

// Identity the loaded conversations belong to; a change means reload from the service.
export const conversationStorageKey = (userId, orgId) => (userId && orgId ? `rhinoanalyst:${userId}:${orgId}` : null);
